import { DatesSetArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import FullCalendar from '@fullcalendar/react';
import BeachAccessOutlinedIcon from '@mui/icons-material/BeachAccessOutlined';
import CakeIcon from '@mui/icons-material/Cake';
import CircleIcon from '@mui/icons-material/Circle';
import { Box, Card, Typography } from '@mui/material';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { handleError } from '../../../shared/functions/handleError';
import i18n from '../../../utils/localization/i18n';
import { AdminEmployeeService } from '../employees/service';
import CalendarFilters from './components/CalendarFilters';

interface ICalendarData {
  start: string;
  end: string;
  title: string;
  color: string;
  type: string;
}
interface ICalendarLegend {
  name: string;
  color: string;
}

export interface ICalendarPagination {
  year: number;
  month: number;
  userId?: number;
}

const initialState: ICalendarData[] = [];
const AdminCalendar = () => {
  const { t } = useTranslation();
  const [calendarLegend, setCalendarLegend] = useState<
    ICalendarLegend[] | null
  >(null);
  const [calendarData, setCalendarData] = useState(initialState);
  const [pagination, setPagination] = useState<ICalendarPagination>({
    year: moment().year(),
    month: moment().month() + 1,
  });
  const changeFilterHandler = (name: string, value: string | number | null) => {
    setPagination((prev) => ({ ...prev, [name]: value }));
  };

  const handleDatesSet = (info: DatesSetArg) => {
    const newDate = moment(info.view.currentStart);
    setPagination((prev: ICalendarPagination) => ({
      ...prev,
      year: newDate.year(),
      month: newDate.month() + 1,
    }));
  };

  useEffect(() => {
    AdminEmployeeService.getCalendarData(pagination)
      .then((response) => {
        setCalendarLegend(response?.legend);
        setCalendarData(response?.data);
      })
      .catch((err) => handleError(err));
  }, [pagination]);
  return (
    <Card>
      <CalendarFilters
        changeFilterHandler={changeFilterHandler}
        pagination={pagination}
      />
      <FullCalendar
        plugins={[dayGridPlugin]}
        locale={i18n.language === 'sr' ? 'sr-ME' : i18n.language}
        buttonText={{
          today: t('general.today'),
          day: t('general.dayView'),
          week: t('general.weekView'),
          month: t('general.monthView'),
        }}
        firstDay={1}
        initialView="dayGridMonth"
        headerToolbar={{ center: 'dayGridDay,dayGridWeek,dayGridMonth' }}
        events={calendarData}
        eventContent={renderEventContent}
        datesSet={handleDatesSet}
      />
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          marginTop: 2,
        }}
      >
        {calendarLegend?.map((legend) => {
          return (
            <Box
              key={legend?.name}
              sx={{ display: 'flex', alignItems: 'center', marginRight: 2 }}
            >
              <CircleIcon sx={{ fill: legend?.color, marginRight: 1 }} />
              <Typography variant={'body2'}>
                {t(`calendar.legend.${legend?.name}`)}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Card>
  );
};

function renderEventContent(eventInfo: any) {
  const { type } = eventInfo?.event?._def?.extendedProps;
  const isBirthday = type === 'birthday';
  const isVacation = type?.includes('vacation');
  return (
    <Box display={'flex'} alignItems={'center'}>
      {isBirthday && (
        <b>
          <CakeIcon fontSize={'small'} />
        </b>
      )}
      {isVacation && <BeachAccessOutlinedIcon />}
      <i>{eventInfo?.event?.title}</i>
    </Box>
  );
}

export default AdminCalendar;
